import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

/* ─── Magnetic product card ──────────────────────────────────────── */
function ProductCard({ title, subtitle, tag, icon, delay = 0, compact = false }) {
  const cardRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 20 });
  const springY = useSpring(y, { stiffness: 200, damping: 20 });
  const rotateX = useTransform(springY, [-40, 40], [5, -5]);
  const rotateY = useTransform(springX, [-40, 40], [-5, 5]);

  const handleMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };
  const handleMouseLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      whileHover={{ scale: 1.02 }}
      className={`bg-white cursor-pointer group relative overflow-hidden border border-border-subtle shadow-card hover:shadow-card-hover hover:border-border-medium transition-all duration-300 ${
        compact ? 'rounded-xl p-3.5' : 'rounded-2xl p-5'
      }`}
    >
      {/* Tag */}
      <span className={`badge bg-accent-light text-accent block w-fit ${compact ? 'text-[9px] mb-2' : 'text-[10px] mb-3'}`}>
        {tag}
      </span>

      {/* Icon */}
      <div className={`rounded-xl bg-accent-light flex items-center justify-center group-hover:bg-accent/10 transition-colors duration-300 ${
        compact ? 'w-8 h-8 mb-2' : 'w-10 h-10 mb-3'
      }`}>
        {icon}
      </div>

      <h3 className={`text-primary font-semibold leading-snug ${compact ? 'text-xs' : 'text-sm'}`}>{title}</h3>
      <p className={`text-secondary leading-relaxed ${compact ? 'text-[11px] mt-0.5' : 'text-xs mt-1'}`}>{subtitle}</p>

      {/* Arrow */}
      <div className={`flex items-center gap-1 text-accent font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 ${
        compact ? 'mt-2 text-[10px]' : 'mt-3 text-xs'
      }`}>
        Learn more
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
        </svg>
      </div>
    </motion.div>
  );
}

/* ─── Particle canvas background ────────────────────────────────── */
function useParticleCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId;
    let particles = [];
    const mouse = { x: null, y: null, radius: 180 };

    const PARTICLE_COLOR = () =>
      document.documentElement.classList.contains('dark')
        ? 'rgba(96, 165, 250, 0.8)'
        : 'rgba(0, 117, 255, 0.75)';
    const LINE_NEAR = (o) =>
      document.documentElement.classList.contains('dark')
        ? `rgba(96, 165, 250, ${o})`
        : `rgba(0, 117, 255, ${o})`;
    const LINE_FAR = (o) =>
      document.documentElement.classList.contains('dark')
        ? `rgba(147, 197, 253, ${o})`
        : `rgba(99, 170, 255, ${o})`;

    class Particle {
      constructor() { this.reset(canvas.width, canvas.height); }
      reset(w, h) {
        this.size = Math.random() * 2 + 1;
        this.x  = Math.random() * (w - this.size * 2) + this.size;
        this.y  = Math.random() * (h - this.size * 2) + this.size;
        this.dx = Math.random() * 0.4 - 0.2;
        this.dy = Math.random() * 0.4 - 0.2;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = PARTICLE_COLOR();
        ctx.fill();
      }
      update(w, h) {
        if (this.x > w || this.x < 0) this.dx = -this.dx;
        if (this.y > h || this.y < 0) this.dy = -this.dy;
        if (mouse.x !== null && mouse.y !== null) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouse.radius + this.size) {
            const f = (mouse.radius - dist) / mouse.radius;
            this.x -= (dx / dist) * f * 5;
            this.y -= (dy / dist) * f * 5;
          }
        }
        this.x += this.dx;
        this.y += this.dy;
        this.draw();
      }
    }

    const init = (w, h) => {
      particles = [];
      const count = Math.floor((w * h) / 9000);
      for (let i = 0; i < count; i++) particles.push(new Particle());
    };

    const connect = (w, h) => {
      const threshold = (w / 7) * (h / 7);
      for (let a = 0; a < particles.length; a++) {
        for (let b = a + 1; b < particles.length; b++) {
          const dx = particles[a].x - particles[b].x;
          const dy = particles[a].y - particles[b].y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < threshold) {
            const opacity = 1 - dist2 / 20000;
            const nearMouse = mouse.x !== null &&
              Math.hypot(particles[a].x - mouse.x, particles[a].y - mouse.y) < mouse.radius;
            ctx.strokeStyle = nearMouse ? LINE_NEAR(opacity) : LINE_FAR(opacity);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
    };

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      init(canvas.width, canvas.height);
    };

    const animate = () => {
      const { width: w, height: h } = canvas;
      const isDark = document.documentElement.classList.contains('dark');
      ctx.fillStyle = isDark ? 'rgb(15,23,42)' : 'rgb(255,255,255)';
      ctx.fillRect(0, 0, w, h);
      particles.forEach(p => p.update(w, h));
      connect(w, h);
      animId = requestAnimationFrame(animate);
    };

    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onOut  = ()  => { mouse.x = null; mouse.y = null; };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseout', onOut);

    resize();
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onOut);
    };
  }, [canvasRef]);
}

/* ─── Main Hero ──────────────────────────────────────────────────── */
const Hero = () => {
  const canvasRef = useRef(null);
  useParticleCanvas(canvasRef);

  const words = ['Smart', 'Logistics,', 'Delivered.'];

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-white pt-20">

      {/* Aether Flow particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Hero content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 flex flex-col items-center text-center">

        {/* Animated headline */}
        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.05] tracking-tight mb-6">
          {['Powering', ...words].map((word, wi) => (
            <span key={wi} className="inline-block mr-4 last:mr-0">
              {word.split('').map((char, ci) => (
                <motion.span
                  key={`${wi}-${ci}`}
                  initial={{ y: 80, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{
                    delay: 0.2 + wi * 0.12 + ci * 0.025,
                    duration: 0.6,
                    type: 'spring',
                    stiffness: 180,
                    damping: 22,
                  }}
                  className={`inline-block ${wi >= 1 ? 'text-gradient-blue' : 'text-primary'}`}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </span>
          ))}
        </h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="text-secondary text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10"
        >
          We deliver intelligent transport management software, seamless booking platforms,
          and compliance tools to empower businesses with efficient,
          sustainable transport solutions.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-8"
        >
          <Link to="/pricing">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="relative inline-flex items-center gap-2 px-8 py-4 bg-accent text-white font-semibold rounded-xl text-base overflow-hidden group cursor-pointer shadow-glow-sm"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
              Get Started Free
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-1 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </motion.button>
          </Link>

          <Link to="/services">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary font-semibold rounded-xl text-base border border-border-medium hover:bg-surface-2 hover:border-border-strong transition-all duration-200 cursor-pointer shadow-card"
            >
              Explore Solutions
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </motion.button>
          </Link>
        </motion.div>

        {/* Product cards grid */}
        <div className="w-full max-w-4xl flex flex-col gap-2 mb-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <Link to="/logistics">
              <ProductCard
                delay={1.4}
                tag="Transport Management"
                title="Clutch Logistics"
                subtitle="Integrated TMS for exporters and large-scale agricultural logistics."
                icon={
                  <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                  </svg>
                }
              />
            </Link>
            <Link to="/bookings">
              <ProductCard
                delay={1.5}
                tag="Smart Scheduling"
                title="Clutch Bookings"
                subtitle="Optimise booking management for time-sensitive industries and site managers."
                icon={
                  <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                }
              />
            </Link>
            <ProductCard
              delay={1.6}
              tag="Mobile Fleet"
              title="Clutch Driver App"
              subtitle="Real-time job assignments, GPS tracking and reporting for drivers."
              icon={
                <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 15.75h3" />
                </svg>
              }
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto w-full"
          >
            <Link to="/contact">
              <ProductCard
                compact
                delay={1.7}
                tag="Built for Growth"
                title="AI for Small & Medium Businesses"
                subtitle="Practical AI tools and automation built for growing businesses."
                icon={
                  <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15m-7.5 18v-9m0-9v9m0 0H9m4.5 0H15M9 12h6" />
                  </svg>
                }
              />
            </Link>
            <Link to="/contact">
              <ProductCard
                compact
                delay={1.8}
                tag="Intelligent Automation"
                title="AI Solutions"
                subtitle="Custom AI development, workflow automation, and predictive analytics."
                icon={
                  <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                }
              />
            </Link>
          </motion.div>
        </div>

      </div>

    </section>
  );
};

export default Hero;
